import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';
import type { QuickBookGroup } from './provider-card';

/**
 * Pick the provider's preferred appointment profile, used as the
 * slot-popover's profileId (PROD-828). Mirrors Flutter
 * `calendar_tab.dart:4324`:
 *
 *   `appointmentProfile.where((p) => p.isPreferred).first ?? appointmentProfile.first`
 *
 * `quickBookGroups` is the grouped-by-location shape the strip uses
 * for chip rendering. This helper flattens then searches for
 * `isPreferred`; falls back to the first profile across all groups.
 *
 * Defensive nullable handling (Samer review §2 on PR #213):
 *   - `groups` accepts `null` / `undefined` for the caller (provider
 *     summaries built before the strip data lands).
 *   - Each group's `profiles` is treated as possibly null/missing —
 *     the TypeScript type marks it required, but at runtime mapping
 *     functions can produce empty / partial shapes.
 *   - Returns `null` (not undefined) when nothing pickable exists,
 *     so the gate predicate stays a single `!!preferredProfile?.id`
 *     check.
 */
export function pickPreferredProfile(
  groups: QuickBookGroup[] | null | undefined,
): CreateAppointmentProfileRequest | null {
  if (!groups || groups.length === 0) return null;
  const all: CreateAppointmentProfileRequest[] = [];
  for (const g of groups) {
    if (!g?.profiles) continue;
    for (const p of g.profiles) {
      if (p) all.push(p);
    }
  }
  if (all.length === 0) return null;
  return all.find((p) => p.isPreferred) ?? all[0] ?? null;
}
